// src/resources/dashboards/Painel360/PointsEconomySection.tsx
import React from 'react';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import SavingsIcon from '@mui/icons-material/Savings';
import dayjs from 'dayjs';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { COLORS, EmptyHint, ErrorCard, SectionHeader, SkeletonCard, cardSx, fmtBrl, fmtNum, fmtPct } from './shared';
import { PointsEconomyData, SectionState } from './types';

// R$ por ponto: valor pequeno (ex.: R$ 0,030) — precisa de casas decimais (fmtBrl arredonda pra 0).
const fmtPointValue = (v: number) => `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}`;

const Stat = ({ label, value, color }: { label: string; value: string; color?: string }) => (
  <Box>
    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: COLORS.faint }}>{label}</Typography>
    <Typography sx={{ fontSize: 22, fontWeight: 800, color: color ?? COLORS.ink }}>{value}</Typography>
  </Box>
);

const PointsEconomySection = ({ state }: { state: SectionState<PointsEconomyData> }) => {
  // Recharts (SVG <text>, tooltip e legend) herda a fonte do <body> (-apple-system),
  // não a do tema MUI usado no resto do painel. Forçar a fonte do tema pra não destoar.
  const theme = useTheme();
  const chartFont = theme.typography.fontFamily;
  if (state.loading) return <SkeletonCard height={360} />;
  if (state.error) return <ErrorCard onRetry={state.refetch} height={360} />;
  if (!state.data) return null;
  const { series, totals, liability, programCost, computing } = state.data;
  // Só o passivo depende do rollup pré-agregado; enquanto computa, liability vem null.
  const liabilityComputing = computing || liability == null;

  const chartData = series.map(s => ({ ...s, label: dayjs(s.weekStart).format('DD/MM') }));

  return (
    <Box>
      <SectionHeader
        icon={<SavingsIcon sx={{ fontSize: 18 }} />}
        gradient={`linear-gradient(135deg, ${COLORS.warning}, #f97316)`}
        title="Economia de pontos — quanto custa, quanto rende"
        subtitle="Pontos gerados, resgatados e expirados + custo real do programa"
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 1.75 }}>
        <Box sx={{ ...cardSx, gridColumn: '1 / -1' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Fluxo de pontos no período</Typography>
          <Typography sx={{ fontSize: 12, color: COLORS.muted, mb: 1 }}>
            Gerados {fmtNum(totals.generated)} · Resgatados {fmtNum(totals.redeemed)} ({fmtPct(totals.redeemedPct)}) · Expirados {fmtNum(totals.expired)} ({fmtPct(totals.expiredPct)})
          </Typography>
          {!chartData.length ? <EmptyHint text="Sem movimentação de pontos no período." /> : (
            <Box sx={{ width: '100%', height: 240, fontFamily: chartFont }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: COLORS.muted, fontFamily: chartFont }} />
                  <YAxis tick={{ fontSize: 11, fill: COLORS.muted, fontFamily: chartFont }} tickFormatter={v => fmtNum(v)} />
                  <Tooltip formatter={(v: any) => fmtNum(v)} contentStyle={{ fontFamily: chartFont, fontSize: 13, borderRadius: 12, border: 'none', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }} />
                  <Legend wrapperStyle={{ fontFamily: chartFont }} />
                  <Area type="monotone" dataKey="generated" name="Gerados" stackId="1" stroke={COLORS.success} fill={COLORS.success} fillOpacity={0.4} />
                  <Area type="monotone" dataKey="redeemed" name="Resgatados" stackId="2" stroke={COLORS.brand} fill={COLORS.brand} fillOpacity={0.4} />
                  <Area type="monotone" dataKey="expired" name="Expirados" stackId="3" stroke={COLORS.danger} fill={COLORS.danger} fillOpacity={0.4} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Box>

        <Box sx={cardSx}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Passivo de pontos</Typography>
          <Typography sx={{ fontSize: 12, color: COLORS.muted, mb: 1.5 }}>Saldo acumulado da base = compromisso futuro</Typography>
          {liabilityComputing ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1 }}>
              <CircularProgress size={18} sx={{ color: COLORS.warning }} />
              <Box>
                <Typography variant="body2" sx={{ color: COLORS.muted }}>Preparando o passivo de pontos…</Typography>
                <Typography sx={{ fontSize: 11.5, color: COLORS.faint }}>Alguns minutos na primeira vez.</Typography>
              </Box>
            </Box>
          ) : (
            <>
              <Typography sx={{ fontSize: 30, fontWeight: 800, color: COLORS.ink }}>{fmtNum(liability.points)} pts</Typography>
              {liability.estimatedValue !== null && (
                <Typography sx={{ fontSize: 12.5, color: COLORS.muted, mt: 0.5 }}>
                  ≈ <b style={{ color: COLORS.ink }}>{fmtBrl(liability.estimatedValue)}</b> em resgates potenciais
                </Typography>
              )}
              {/* De onde veio a conta: valor do cashback por ponto da rede (cashback_factor). */}
              <Typography sx={{ fontSize: 11, color: COLORS.faint, mt: 0.75 }}>
                {liability.pointValueConfigured ? (
                  <>Base: {fmtPointValue(liability.pointValue)} por ponto (cashback configurado da rede).</>
                ) : (
                  <><b style={{ color: COLORS.warning }}>Valor por ponto não configurado nesta rede.</b> Estimativa usando {fmtPointValue(liability.pointValue)}/ponto como base — configure o cashback por ponto para o valor real.</>
                )}
              </Typography>
            </>
          )}
        </Box>

        <Box sx={cardSx}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Custo do programa × retorno</Typography>
          <Typography sx={{ fontSize: 12, color: COLORS.muted, mb: 1.5 }}>Descontos + cashback + cupons vs faturamento identificado</Typography>
          <Box sx={{ display: 'flex', gap: 3.5, flexWrap: 'wrap' }}>
            <Stat label="Custo total" value={fmtBrl(programCost.total)} />
            <Stat label="% do faturamento" value={fmtPct(programCost.pctOfRevenue, 1)} color={COLORS.success} />
            <Stat label="Receita por R$ 1 investido" value={programCost.revenuePerReal !== null ? fmtBrl(programCost.revenuePerReal) : '—'} color={COLORS.brand} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PointsEconomySection;
